import React, { useEffect, useRef, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { makeLobbyCode, buildJoinLink } from "../constants";

function slugify(s) {
  return String(s || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "")
    .slice(0, 24);
}

function Teams({ hunt, updateHunt, currentTheme, themeMeta, organizationId, sessionId }) {
  const [copiedLink, setCopiedLink] = useState(false);
  const [copiedCode, setCopiedCode] = useState(false);
  const qrRef = useRef(null);

  // Generate the lobby code once real theme data has loaded, if this hunt doesn't have one yet.
  // It stays fixed after that — teams and printed materials should be able to rely on it.
  useEffect(() => {
    if (!themeMeta) return;
    if (!hunt.lobbyCode) {
      updateHunt({ lobbyCode: makeLobbyCode() });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [themeMeta, hunt.lobbyCode]);

  const joinLink = buildJoinLink({ organizationId, sessionId, lobbyPin: hunt.lobbyCode });

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(joinLink);
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 1500);
    } catch {
      // clipboard unavailable — link is still selectable in the input
    }
  };

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(hunt.lobbyCode || "");
      setCopiedCode(true);
      setTimeout(() => setCopiedCode(false), 1500);
    } catch {
      // clipboard unavailable — code is still selectable
    }
  };

  const downloadQr = () => {
    const canvas = qrRef.current?.querySelector("canvas");
    if (!canvas) return;
    const a = document.createElement("a");
    a.href = canvas.toDataURL("image/png");
    a.download = `${slugify(hunt.huntName) || "hunt"}_QR-code.png`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  return (
    <div className="studio-step-page" data-tour="studio-step-teams">
      <h2 className="studio-h2">Teams</h2>
      <p className="studio-sub">Share the join link or lobby code so teams can join.</p>

      <div className="studio-teams-layout">
        <div className="studio-side-card studio-teams-qr-card">
          <div className="studio-side-eyebrow">Join link</div>
          {joinLink ? (
            <>
              <div className="studio-qr-frame" ref={qrRef}>
                <QRCodeCanvas value={joinLink} size={176} level="M" includeMargin={false} />
              </div>
              <input className="studio-input" readOnly value={joinLink} onClick={(e) => e.target.select()} />
              <div className="studio-teams-btn-row">
                <button className="studio-btn studio-btn--ghost" onClick={copyLink}>
                  {copiedLink ? "Copied" : "Copy link"}
                </button>
                <button className="studio-btn studio-btn--ghost" onClick={downloadQr}>
                  Download QR
                </button>
              </div>
              <div className="studio-hint">Link goes live when you publish. Print the QR for the registration desk.</div>
            </>
          ) : (
            <div className="studio-hint">
              Guest join link is not configured for this environment. Share the lobby code below instead.
            </div>
          )}
        </div>

        <div className="studio-side-card studio-teams-code-card">
          <div className="studio-side-eyebrow">Lobby code</div>
          <div className="studio-lobby-code">{String(hunt.lobbyCode || "------").split("").join(" ")}</div>
          <button className="studio-btn studio-btn--ghost" style={{ width: "100%" }} onClick={copyCode}>
            {copiedCode ? "Copied" : "Copy code"}
          </button>
          <div className="studio-hint">Teams can enter this 6-digit code on the Join Lobby screen after logging in.</div>
        </div>
      </div>
    </div>
  );
}

export default Teams;
